import axios, { axiosPublic } from '../axiosInterceptor';
import { ModuleInfoResponse } from '../models/ModuleInfoResponse';
import { AxiosResponse, GenericAbortSignal } from 'axios';
import { MenuResponse } from '../models/MenuResponse';
import {
  DataResponse,
  DynamicDataParams,
  DynamicDataRequest,
  DynamicDataResponse,
  ExecuteActionParams,
  ExecuteStaticParams,
  SearchDataParams,
} from '../models/DynamicDataParams';
import { RequestIdentity } from '../models/RequestIdentity';
import { clientId } from './auth';
import { AuthConfig } from '../models/auth/AuthConfig';
import { SelectableElement } from '../components/Widgets/Declaration/DropdownWidget/models/item';
import { DynamicDataEndpointType } from '../models/ViewEditorDataDynamicDataType';
import { ViewResponse } from '../models/viewResponse/ViewResponse';
import {
  ActionExecutionResult,
  SearchExecutionResult,
} from '../models/Widget/actions/ActionExecutionResult';
import { ModuleVersionResponse } from '../models/ModuleVersionResponse';
import { ModuleHealthStatusResponse } from '../models/ModuleHealthStatusResponse';
import { UpdateModuleRequest } from '../models/UpdateModuleRequest';
import { OAuth2Claims } from '../models/auth/OAuth2Claims';
import { ViewStateUrlContext } from '../utils/getViewStateFromUrl';
import { ChangePasswordData, ChangePasswordResponse, ProfileData } from '../models/profile';
import { clearVerifier, loadVerifier } from './pkce';
import { PageActionResponse } from '../models/Widget/actions/PageActionResponse';
import { ModuleStatusResponse } from '../models/ModuleStatusResponse';
import { RuntimePermissionsResponse } from '../models/RuntimePermissionsResponse';

const BASE_PATH: string = '/pbs/api/v1/modules';

//region Module Controller
/**
 * Список модулей
 */
export const getModules = (): Promise<AxiosResponse<Array<ModuleInfoResponse>>> => {
  return axios.get(BASE_PATH);
};

/**
 * Информация о модуле
 */
export const getModuleInfo = (moduleName: string): Promise<AxiosResponse<ModuleInfoResponse>> => {
  return axios.get(`${BASE_PATH}/${moduleName}`);
};

/**
 * Информация о сборке модуля
 */
export const getModuleVersion = (
  moduleName: string,
): Promise<AxiosResponse<ModuleVersionResponse>> => {
  return axios.get(`${BASE_PATH}/${moduleName}/version`);
};

/**
 * Логи модуля. Для мастера имя модуля не нужно указывать
 */
export const getModuleLogs = (moduleName: string | null): Promise<AxiosResponse<any>> => {
  if (moduleName) {
    return axios.get(`${BASE_PATH}/logs?module_name=${moduleName}`);
  }
  return axios.get(`${BASE_PATH}/logs`);
};

/**
 * Жив ли модуль
 */
export const getModuleHealth = (
  moduleName: string,
): Promise<AxiosResponse<ModuleHealthStatusResponse>> => {
  return axios.get(`${BASE_PATH}/${moduleName}/health`);
};

/**
 * Статус доступности модуля
 */
export const getModuleStatus = (moduleName: string): Promise<ModuleStatusResponse> => {
  return axios.get(`${BASE_PATH}/${moduleName}/status`).then((response) => response.data);
};

/**
 * Обновить основную информацию о модуле
 */
export const updateModule = (
  moduleName: string,
  body: UpdateModuleRequest,
): Promise<AxiosResponse<ActionExecutionResult>> => {
  return axios.put(`${BASE_PATH}/${moduleName}`, body);
};

export const getRuntimePermissions = async (
  signal?: AbortSignal,
): Promise<RuntimePermissionsResponse> => {
  const response = await axios.get<RuntimePermissionsResponse>(`${BASE_PATH}/permissions`, {
    signal,
  });

  return response.data;
};
//endregion

//region Auth
export const getAuthConfig = (): Promise<AxiosResponse<AuthConfig>> => {
  return axiosPublic.get(`${BASE_PATH}/auth`);
};

export const getToken = (code: string) => {
  const redirect_uri = window.location.origin + '/callback';
  const verifier = loadVerifier();
  if (!verifier) {
    throw new Error('PKCE verifier not found (sessionStorage). Start login again.');
  }

  const params = new URLSearchParams();
  params.set('grant_type', 'authorization_code');
  params.set('redirect_uri', redirect_uri);
  params.set('code', code);
  params.set('client_id', clientId);
  params.set('code_verifier', verifier);

  clearVerifier();

  return axios.post('/oauth2/token', params.toString(), {
    headers: { 'Content-type': 'application/x-www-form-urlencoded' },
  });
};

export const fetchRefreshToken = (refreshToken: string) => {
  const params = new URLSearchParams();
  params.set('grant_type', 'refresh_token');
  params.set('refresh_token', refreshToken);
  params.set('client_id', clientId);
  return axiosPublic.post('/oauth2/token', params.toString(), {
    headers: { 'Content-type': 'application/x-www-form-urlencoded' },
  });
};

export const getUserClaims = (idToken: string): Promise<AxiosResponse<OAuth2Claims>> => {
  try {
    const payload = idToken.split('.')[1];
    const bytes = Uint8Array.from(atob(payload.replace(/-/g, '+').replace(/_/g, '/')), (c) =>
      c.charCodeAt(0),
    );
    const decoded = JSON.parse(new TextDecoder('utf-8').decode(bytes));
    return Promise.resolve({
      data: decoded,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {},
    } as AxiosResponse<OAuth2Claims>);
  } catch {
    return Promise.reject(new Error('Failed to decode id_token'));
  }
};
//endregion

//region User Change Settings

export const changePassword = async (
  changeData: ChangePasswordData,
): Promise<ChangePasswordResponse> => {
  try {
    const requestData = {
      oldPassword: changeData.current,
      newPassword: changeData.new,
      confirmPassword: changeData.confirm,
      'client-id': clientId,
    };

    const response = await axios.post<ChangePasswordResponse>(
      `${BASE_PATH}/USER_MODULE/base/executeAction`,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          actionName: 'changePassword',
          type: 'UserDataView',
          configName: 'UserDataView',
        },
      },
    );

    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Текущий пароль неверен');
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Некорректные данные');
      }
    }
    throw new Error('Не удалось изменить пароль. Попробуйте позже.');
  }
};

export const updateProfile = async (data: ProfileData): Promise<ProfileData> => {
  try {
    const response = await axios.post(`${BASE_PATH}/USER_MODULE/base/executeAction`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      params: {
        actionName: 'updateProfile',
        type: 'UserDataView',
        configName: 'UserDataView',
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update profile');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      switch (error.response.status) {
        case 409:
          throw new Error('Пользователь с таким email уже существует');
        case 401:
          throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
        case 403:
          throw new Error('Недостаточно прав для изменения профиля');
        case 400:
          throw new Error(error.response.data.message || 'Некорректные данные');
        case 404:
          throw new Error('Профиль не найден');
        case 500:
          throw new Error('Внутренняя ошибка сервера. Попробуйте позже.');
        default:
          throw new Error(`Ошибка сервера: ${error.response.status}`);
      }
    } else if (error.request) {
      throw new Error('Нет ответа от сервера. Проверьте подключение к интернету.');
    } else {
      throw new Error('Ошибка при отправке запроса: ' + error.message);
    }
  }
};
//endregion

//region Base Contract

/**
 Получение данных для формы или страницы (при изменении сущности при помощи формы
 или при открытии страницы после нажатию на дерево)
 */
export const getData = <T extends DataResponse = DataResponse>(
  moduleKey: string,
  params: RequestIdentity,
  abortSignal?: GenericAbortSignal,
): Promise<T> => {
  return axios
    .get(`${BASE_PATH}/${moduleKey}/base/data`, { params, signal: abortSignal || undefined })
    .then((response) => {
      return response?.data;
    });
};

/**
 * Получение представления
 */
export const getConfig = (
  moduleKey: string,
  params: RequestIdentity,
  abortSignal?: GenericAbortSignal,
): Promise<ViewResponse> => {
  return axios
    .get(`${BASE_PATH}/${moduleKey}/base/config`, {
      params: params,
      signal: abortSignal || undefined,
    })
    .then((response) => response?.data);
};

/**
 Получение данных для отдельных виджетов (дерево, таблица)
 */
export const getDynamicData = <T extends DataResponse = DataResponse>(
  moduleKey: string,
  params: DynamicDataParams,
  body: DynamicDataRequest | null,
  abortSignal?: AbortSignal,
): Promise<DynamicDataResponse<T>> => {
  const data = body || {};
  return axios
    .post(`${BASE_PATH}/${moduleKey}/base/dynamicData`, data, { params, signal: abortSignal })
    .then((response) => response.data);
};

/**
 Выполнить метод поиска
 */
export const getDynamicSearch = <T extends DataResponse = DataResponse>(
  moduleKey: string,
  params: SearchDataParams,
  body: DynamicDataRequest | null,
  abortSignal?: AbortSignal,
): Promise<SearchExecutionResult<T>> => {
  const data = body || {};
  return axios
    .post(`${BASE_PATH}/${moduleKey}/base/executeStaticFunction`, data, {
      params,
      signal: abortSignal,
    })
    .then((response) => response.data);
};

/**
 * Создание, изменение данных при помощи формы
 */
export const executePageAction = <T = PageActionResponse>(
  moduleKey: string,
  params: ExecuteActionParams,
  body: any,
): Promise<AxiosResponse<ActionExecutionResult<T>>> => {
  const data = body || {};
  return axios.post(`${BASE_PATH}/${moduleKey}/base/executeAction`, data, { params });
};

export const executeStaticFunction = <T = PageActionResponse>(
  moduleKey: string,
  params: ExecuteStaticParams,
  body: any,
): Promise<AxiosResponse<ActionExecutionResult<T>>> => {
  const data = body || {};
  return axios.post(`${BASE_PATH}/${moduleKey}/base/executeStaticFunction`, data, { params });
};

export const downloadFile = (
  moduleKey: string,
  params: ExecuteActionParams,
  body: any,
): Promise<any> => {
  const data = body || {};
  return axios.post(`${BASE_PATH}/${moduleKey}/base/download`, data, {
    params,
    responseType: 'blob',
  });
};

export const getMenus = (
  moduleName: string,
  parentId?: number,
): Promise<AxiosResponse<Array<MenuResponse>>> => {
  return axios.get(`${BASE_PATH}/${moduleName}/base/menus`, {
    params: parentId ? { parentId } : {},
  });
};

/**
 * Получение uuid для построения конфигурации страницы и хлебных крошек
 */
export const getLink = (uuid: string): Promise<AxiosResponse<ViewStateUrlContext>> => {
  return axios.get('/pbs/api/v1/link', {
    params: { uuid: encodeURIComponent(uuid) },
  });
};

/**
 * Генерация uuid ссылки для ее передачи
 */
export const createLink = (context: string): Promise<AxiosResponse<string>> => {
  return axios.put('/pbs/api/v1/link', { context: context }, {});
};

/** Обёртка над getDynamicData для получения списка опшенов селектов.
 Юзать для запросов ТОЛЬКО ВНУТРИ КОНСТРУКТОРА **/
export const getViewEditorDynamicData = <T extends DataResponse = SelectableElement>(
  moduleKey: string,
  formContextId: string,
  requestBody: DynamicDataRequest,
  endpointType: DynamicDataEndpointType,
  type: string = 'ViewEditorDynamicDataView',
) => {
  const requestParams: DynamicDataParams = {
    type,
    fieldName: endpointType,
    configName: type,
    id: formContextId,
  };

  return getDynamicData<T>(moduleKey, requestParams, requestBody);
};

export const getAvailableThemes = () => {
  return axios.get('/pbs/api/v1/modules/style/all');
};

export const getTheme = (uuid: string) => {
  return axios.get(`/pbs/api/v1/modules/style/${uuid}`);
};

export const updateTheme = (uuid: string, payload: unknown) => {
  return axios.put(`/pbs/api/v1/modules/style/${uuid}`, payload);
};

export const setDefaultUserTheme = (uuid: string) => {
  return axios.put(`/pbs/api/v1/modules/style/current/${uuid}`);
};

export const getDefaultUserTheme = (favTheme: 'light' | 'dark') => {
  return axios.get(`/pbs/api/v1/modules/style`, { headers: { 'X-Theme': favTheme } });
};

export const saveTheme = (payload: unknown) => {
  return axios.post(`/pbs/api/v1/modules/style`, payload);
};

export const deleteTheme = (uuid: string) => {
  return axios.delete(`/pbs/api/v1/modules/style/${uuid}`);
};
